// 题库相关类型定义

// 题目类型枚举 - 与 Prisma 生成的枚举保持一致
export enum QuestionType {
  SINGLE_CHOICE = "SINGLE_CHOICE",
  MULTIPLE_CHOICE = "MULTIPLE_CHOICE",
  SHORT_ANSWER = "SHORT_ANSWER",
  ESSAY = "ESSAY",
}

// 难度等级枚举 - 与 Prisma 生成的枚举保持一致
export enum DifficultyLevel {
  EASY = "EASY",
  MEDIUM = "MEDIUM",
  HARD = "HARD",
}

export type ContentStatus = "PENDING" | "APPROVED" | "REJECTED" | "ACTIVE" | "INACTIVE"
export type TestType = "PRACTICE" | "MOCK_EXAM" | "RANDOM_TEST"
// 注意：CHAPTER_TEST 已迁移到独立的章节练习系统
export type SessionStatus = "IN_PROGRESS" | "COMPLETED" | "ABANDONED" | "TIMEOUT"

// 章节接口
export interface Chapter {
  id: string
  subjectId: string
  name: string
  description?: string
  sortOrder: number
  status: ContentStatus
  createdBy: string
  createdAt: Date
  updatedAt: Date
}

// 基础题目接口
export interface BaseQuestion {
  id: string
  subjectId: string
  chapterId?: string
  type: QuestionType
  difficulty: DifficultyLevel
  content: string
  explanation?: string
  knowledgePoints: string[]
  // 材料题相关字段
  isMaterial: boolean
  materialContent?: string
  materialType?: "TEXT" | "IMAGE" | "CHART"
  materialMetadata?: Record<string, any>
  sortOrder: number
  isRealExam: boolean
  examYear?: string
  frequency: number
  usageCount: number
  correctRate: number
  status: ContentStatus
  createdBy: string
  createdAt: Date
  updatedAt: Date
}

// 选择题
export interface SingleChoiceQuestion extends BaseQuestion {
  type: QuestionType.SINGLE_CHOICE
  options: string[]
  correctAnswer: number // 正确答案索引
}

export interface MultipleChoiceQuestion extends BaseQuestion {
  type: QuestionType.MULTIPLE_CHOICE
  options: string[]
  correctAnswer: number[] // 正确答案索引数组
}

// 简答题
export interface ShortAnswerQuestion extends BaseQuestion {
  type: QuestionType.SHORT_ANSWER
  correctAnswer: string | string[] // 可能有多个正确答案
  keywords?: string[] // 关键词，用于自动评分
}

// 论文题
export interface EssayQuestion extends BaseQuestion {
  type: QuestionType.ESSAY
  requirements: {
    minWords?: number
    maxWords?: number
    timeLimit?: number // 分钟
    keyPoints?: string[] // 关键要点
  }
  rubric?: {
    criteria: Array<{
      name: string
      description: string
      maxScore: number
    }>
  }
}

// 联合类型
export type Question =
  | SingleChoiceQuestion
  | MultipleChoiceQuestion
  | ShortAnswerQuestion
  | EssayQuestion

// 用户答题记录
export interface QuestionAnswer {
  id: string
  userId: string
  questionId: string
  sessionId?: string
  userAnswer: any
  isCorrect: boolean
  timeSpent: number // 秒
  answeredAt: Date
}

// 测试会话
export interface TestSession {
  id: string
  userId: string
  subjectId: string
  questionBankId?: string
  sessionType: TestType
  questions: string[] // 题目ID列表
  answers?: Record<string, any>
  score?: number
  totalQuestions: number
  correctCount: number
  startTime: Date
  endTime?: Date
  status: SessionStatus
}

// 测试会话答题记录
export interface TestSessionAnswer {
  id: string
  sessionId: string
  questionId: string
  userAnswer: any
  isCorrect: boolean
  timeSpent: number
  answeredAt: Date
}

// 每日练习记录
export interface DailyPractice {
  id: string
  userId: string
  date: Date
  completed: boolean
  totalQuestions: number
  correctCount: number
  timeSpent: number
  sessionId?: string
  createdAt: Date
  updatedAt: Date
}

// 每日练习统计
export interface DailyPracticeStats {
  totalDays: number
  completedDays: number
  currentStreak: number
  longestStreak: number
  totalQuestions: number
  totalCorrect: number
  averageAccuracy: number
}

// 组卷配置
export interface ExamConfig {
  subjectId: string
  chapterId?: string // 可选的章节ID，用于章节练习
  totalQuestions: number
  difficultyDistribution: {
    easy: number
    medium: number
    hard: number
  }
  typeDistribution: {
    singleChoice: number
    multipleChoice: number
    shortAnswer: number
    essay: number
  }
  knowledgePoints?: string[]
  excludeQuestionIds?: string[] // 排除的题目ID
  timeLimit?: number // 考试时长（分钟）
}

// 答题统计
export interface AnswerStatistics {
  totalQuestions: number
  correctCount: number
  wrongCount: number
  unansweredCount: number
  correctRate: number
  averageTimePerQuestion: number
  totalTimeSpent: number
  difficultyBreakdown: {
    easy: { total: number; correct: number }
    medium: { total: number; correct: number }
    hard: { total: number; correct: number }
  }
  typeBreakdown: {
    singleChoice: { total: number; correct: number }
    multipleChoice: { total: number; correct: number }
    shortAnswer: { total: number; correct: number }
    essay: { total: number; correct: number }
  }
  knowledgePointBreakdown: Record<string, { total: number; correct: number }>
}

// 学习建议
export interface StudySuggestion {
  weakKnowledgePoints: string[]
  recommendedDifficulty: DifficultyLevel
  suggestedPracticeTypes: QuestionType[]
  estimatedStudyTime: number // 小时
  nextSteps: string[]
}

// API响应类型
export interface QuestionListResponse {
  questions: Question[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

// 创建题目的请求类型
export interface CreateQuestionRequest {
  subjectId: string
  chapterId?: string
  materialId?: string
  type: QuestionType
  difficulty: DifficultyLevel
  content: string
  options?: string[]
  correctAnswer: any
  explanation?: string
  knowledgePoints: string[]
  sortOrder?: number
}

// 创建材料的请求类型
export interface CreateMaterialRequest {
  subjectId: string
  title: string
  content: string
  materialType: "TEXT" | "IMAGE" | "CHART"
  metadata?: Record<string, any>
}

// 批量导入题目的请求类型
export interface ImportQuestionsRequest {
  subjectId: string
  questions: CreateQuestionRequest[]
  materials?: CreateMaterialRequest[]
}

// 每日练习记录
export interface DailyPractice {
  id: string
  userId: string
  date: Date
  completed: boolean
  totalQuestions: number
  correctCount: number
  timeSpent: number
  sessionId?: string
  createdAt: Date
  updatedAt: Date
}

// 章节练习状态（用于UI显示）
export enum ChapterPracticeStatus {
  NEVER_STARTED = "NEVER_STARTED", // 从未练习过
  IN_PROGRESS = "IN_PROGRESS", // 有未完成的会话
  COMPLETED_OUTDATED = "COMPLETED_OUTDATED", // 已完成但题库有更新
  COMPLETED_CURRENT = "COMPLETED_CURRENT", // 已完成且是最新的
}

// 章节练习数据库状态
export type ChapterPracticeDbStatus = "IN_PROGRESS" | "COMPLETED" | "ABANDONED"

// 章节练习类型
export type ChapterPracticeType = "FIRST_TIME" | "RETRY" | "NEW_QUESTIONS"

// 章节进度
export interface ChapterProgress {
  chapterId: string
  chapterName: string
  total: number
  answered: number
  correct: number
  progress: number // 百分比
  mastery: number // 掌握度百分比
}

// 章节练习状态详情
export interface ChapterPracticeState {
  chapterId: string
  status: ChapterPracticeStatus
  progress: ChapterProgress
  activeSessionId?: string // 未完成的会话ID
  lastPracticeDate?: Date
  totalQuestionsAtLastPractice?: number // 上次练习时的题目总数
  newQuestionsCount: number // 新增题目数量
  canContinue: boolean // 是否可以继续未完成的会话
  canStartNew: boolean // 是否可以开始新练习
}

// 每日练习配置
export interface DailyPracticeConfig {
  totalQuestions: number
  difficultyDistribution: {
    easy: number
    medium: number
    hard: number
  }
  typeDistribution: {
    choice: number // 选择题（单选+多选）
    analysis: number // 案例分析题
  }
  focusWeakPoints: boolean
  excludeRecent: number // 排除最近N天做过的题
}

// 章节练习记录
export interface ChapterPractice {
  id: string
  userId: string
  chapterId: string
  status: ChapterPracticeDbStatus
  practiceType: ChapterPracticeType
  startTime: Date
  endTime?: Date
  totalQuestions: number
  correctCount: number
  score?: number
  questionSnapshot: Question[] // 题目快照
  createdAt: Date
  updatedAt: Date
}

// 章节练习答题记录
export interface ChapterPracticeAnswer {
  id: string
  practiceId: string
  questionId: string
  userAnswer: any
  isCorrect: boolean
  timeSpent: number
  answeredAt: Date
}

// 用户每日打卡记录
export interface UserDailyCheckin {
  id: string
  userId: string
  date: Date
  createdAt: Date
  updatedAt: Date
}
